import { MemberRole } from '@/__generated__/graphql';
import { EVERY_ROLE_SURFACE } from '@/lib/dashboard-permissions';

import type { WiredSidebarLabsFlag } from '@/core/AppShell/sidebar-labs-flags';
import type { DashboardAction, DashboardSurface } from '@/lib/dashboard-permissions';
import type { SettingAnchor, SettingControl } from '@/lib/setting-anchors.types';
import type { Locale } from '@syncanix/shared';

/**
 * Who may be OFFERED a setting — one function, read by both search surfaces.
 *
 * The rule it enforces is the registry's oldest promise: **a search never offers
 * a jump that lands on a card the caller cannot see.** A dead end is worse than
 * an absent result, because an absent result sends somebody to look elsewhere
 * and a dead end tells them the setting is not there.
 *
 * It is one function for the same reason the index is one index. This gate
 * absorbed `useSettingCommands`' private `isReachable`, which had drifted: the
 * palette offered the three `/experience` settings while the sidebar hid the row
 * and the route itself 404s, so the surface whose whole job is finding a setting
 * was the only way into a page the workspace had not switched on.
 *
 * The two fail postures differ on purpose, and each matches the mechanism it
 * mirrors:
 *
 *  - **RBAC fails SAFE.** `can()` returns `true` while the role loads, so an
 *    entitled operator never watches results appear a beat late.
 *  - **Labs fails CLOSED**, exactly as the sidebar's own gate does: hidden while
 *    the flag loads, hidden on an error, hidden for an id the server does not
 *    list. Offering a setting on a page that answers 404 is the worse mistake.
 */

export interface SettingsSearchGateContext {
  /** Fail-safe permission check — `usePermissions().can`. */
  readonly can: (surface: DashboardSurface, action: DashboardAction) => boolean;
  /** The caller's role, or `null` while it loads. */
  readonly role: MemberRole | null;
  /** Whether the active workspace is the personal one. */
  readonly isPersonal: boolean;
  /** Whether the operator is in Advanced control mode (`ui-design.md` §20). */
  readonly isAdvanced: boolean;
  /** The wired Labs flags, resolved — the same record the sidebar builds. */
  readonly labsFlags: Readonly<Record<WiredSidebarLabsFlag, boolean>>;
  /** The active locale, for a control that only exists in some. */
  readonly locale: Locale;
}

/** May this caller be offered this setting? */
export function isSearchable(anchor: SettingAnchor, ctx: SettingsSearchGateContext): boolean {
  // `/account`'s preferences belong to whoever is signed in rather than to any
  // workspace grant, so there is no surface to ask about.
  if (anchor.surface !== EVERY_ROLE_SURFACE && !ctx.can(anchor.surface, anchor.action)) {
    return false;
  }
  if (anchor.requiresNonPersonal === true && ctx.isPersonal) return false;
  // Unknown role → allow, matching `can`'s fail-safe rather than hiding a result
  // from an owner mid-load.
  if (anchor.requiresOwner === true && ctx.role !== null && ctx.role !== MemberRole.Owner) {
    return false;
  }
  if (anchor.labsFlag !== undefined && !ctx.labsFlags[anchor.labsFlag]) return false;
  // §20 at CARD scale: the Studio's Component styling pane is advanced-only in
  // its entirety, not one control inside something Simple can see.
  if (anchor.requiresAdvanced === true && !ctx.isAdvanced) return false;
  return true;
}

/**
 * May this caller be offered this control?
 *
 * Assumes its card already passed {@link isSearchable} — a control cannot be
 * more reachable than the card it lives in.
 *
 * Simple mode HIDES an advanced-only control rather than offering it with a
 * badge or a mode switch. `ui-design.md` §20 is explicit that the mode's whole
 * design is absence, not collapse, and a search result that silently flipped
 * somebody into Advanced would be the search changing a preference about the
 * person to satisfy a query.
 */
export function isControlSearchable(
  control: SettingControl,
  ctx: SettingsSearchGateContext,
): boolean {
  if (control.requiresAdvanced === true && !ctx.isAdvanced) return false;
  if (control.localeOnly !== undefined && !control.localeOnly.includes(ctx.locale)) return false;
  return true;
}
